import { Routes } from "@/features/router";
import {
  EyeIcon,
  IdentificationIcon,
  CreditCardIcon,
  DevicePhoneMobileIcon
} from "@heroicons/react/20/solid";
import _ from "lodash";
import type React from "react";
import { useTranslation } from "react-i18next";
import { twMerge } from "tailwind-merge";
import { MenuItem } from "./index.react";
import { ChevronLeftIcon, DocumentIcon, EnvelopeIcon, HomeIcon } from "@heroicons/react/24/solid";
import { CardTitle, Info } from "@/atoms-react/text/Text.react";
import { useMediaQuery } from "usehooks-ts";
import { useCompanyContext } from "@/features/company/hooks/use-company-context";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import { OlkyPassProTab, setOlkypassPassProTab } from "@/redux/slices/companyOnboardingSlice";
import { useLocation } from "react-router-dom";
import { getRoute } from "@/utils/routes";

type MenuAccountProps = {
  open: boolean;
  onClose: () => void;
  isCompanyOnboarding?: boolean;
} & React.ComponentPropsWithoutRef<"div">;

export const MenuAccount = ({
  open,
  onClose,
  isCompanyOnboarding = false,
  ...props
}: MenuAccountProps) => {
  const { t } = useTranslation(["pass"]);
  const isNotMobile = useMediaQuery("(min-width: 768px)");
  const { leUuid } = useCompanyContext();
  const dispatch = useAppDispatch();
  const { pathname } = useLocation();

  const menuItemOnclick = (tab: OlkyPassProTab) =>
    isCompanyOnboarding ? () => dispatch(setOlkypassPassProTab(tab)) : undefined;

  const activeMenuItem = useAppSelector((state) => state.companyOnboarding.olkypassPassProTab);

  const forceIsActive =
    !!leUuid && pathname.includes(getRoute(Routes.company_onboarding, { company: leUuid }));

  return (
    <div
      className={twMerge(
        "absolute h-full w-64 shrink-0 border-slate-50 border-r bg-white px-4 py-6 lg:relative lg:w-80 lg:px-8 dark:border-slate-900 dark:bg-slate-950",
        "dark: -translate-x-full transition-all lg:translate-x-0",
        "z-50 overflow-auto",
        open ? "translate-x-0" : ""
      )}
      {..._.omit(props, "className")}
    >
      <div
        className={twMerge("mb-4 flex cursor-pointer items-center gap-x-1", isNotMobile && "h-12")}
        onClick={onClose}
      >
        <ChevronLeftIcon className="h-5 w-5 text-black dark:text-white" />
        <CardTitle className="block">
          {leUuid
            ? t("olkypass.mainPage.navigation_menu.link.home")
            : t("olkypass.mainPage.navigation_menu.link.my_pass")}
        </CardTitle>
      </div>
      {isNotMobile && <Info className="mt-10 block"></Info>}

      <MenuItem
        to={leUuid ? `${Routes.level}/${leUuid}` : Routes.level}
        onClick={menuItemOnclick(OlkyPassProTab.Level)}
        isActive={forceIsActive && activeMenuItem === OlkyPassProTab.Level}
        icon={(p) => <IdentificationIcon {...p} />}
      >
        {t("olkypass.mainPage.navigation_account_menu.link.level")}
      </MenuItem>
      <MenuItem
        to={leUuid ? `${Routes.account_alias_documents}/${leUuid}` : Routes.account_alias_documents}
        onClick={menuItemOnclick(OlkyPassProTab.Documents)}
        isActive={forceIsActive && activeMenuItem === OlkyPassProTab.Documents}
        icon={(p) => <DocumentIcon {...p} />}
        data-cy="navigation-menu-item-document"
      >
        Documents
      </MenuItem>
      <MenuItem
        to={leUuid ? `${Routes.account_alias_email}/${leUuid}` : Routes.account_alias_email}
        onClick={menuItemOnclick(OlkyPassProTab.Emails)}
        isActive={forceIsActive && activeMenuItem === OlkyPassProTab.Emails}
        icon={(p) => <EnvelopeIcon {...p} />}
        data-cy="navigation-menu-item-mail"
      >
        {t("olkypass.mainPage.navigation_account_menu.link.alias_email")}
      </MenuItem>

      <MenuItem
        dataCy="navigation-menu-alias-address"
        to={leUuid ? `${Routes.account_alias_address}/${leUuid}` : Routes.account_alias_address}
        onClick={menuItemOnclick(OlkyPassProTab.Address)}
        isActive={forceIsActive && activeMenuItem === OlkyPassProTab.Address}
        icon={(p) => <HomeIcon {...p} />}
        data-cy="navigation-menu-item-address"
      >
        {t("olkypass.mainPage.navigation_account_menu.link.alias_address")}
      </MenuItem>

      <MenuItem
        dataCy="navigation-menu-alias-mobiles"
        to={leUuid ? `${Routes.account_alias_mobile}/${leUuid}` : Routes.account_alias_mobile}
        onClick={menuItemOnclick(OlkyPassProTab.Mobiles)}
        isActive={forceIsActive && activeMenuItem === OlkyPassProTab.Mobiles}
        icon={(p) => <DevicePhoneMobileIcon {...p} />}
        data-cy="navigation-menu-item-mobile"
      >
        {t("olkypass.mainPage.navigation_account_menu.link.alias_mobiles")}
      </MenuItem>
      <MenuItem
        to={leUuid ? `${Routes.account_alias_payments}/${leUuid}` : Routes.account_alias_payments}
        onClick={menuItemOnclick(OlkyPassProTab.Ibans)}
        isActive={forceIsActive && activeMenuItem === OlkyPassProTab.Ibans}
        icon={(p) => <CreditCardIcon {...p} />}
        data-cy="navigation-menu-item-iban"
      >
        {t("olkypass.mainPage.navigation_account_menu.link.alias_payments")}
      </MenuItem>
      <MenuItem
        to={leUuid ? `${Routes.account_alias_public}/${leUuid}` : Routes.account_alias_public}
        onClick={menuItemOnclick(OlkyPassProTab.PublicVisibility)}
        isActive={forceIsActive && activeMenuItem === OlkyPassProTab.PublicVisibility}
        icon={(p) => <EyeIcon {...p} />}
        data-cy="navigation-menu-item-public"
      >
        {t("olkypass.mainPage.navigation_account_menu.link.alias_public")}
      </MenuItem>
    </div>
  );
};
